import { computed, unref } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * The receipts behind a protocol's numbers: which document supports which
 * limit, numbered in the order the limits are read.
 *
 * Extracted from ClinicalSettings.vue when the source list moved out of a
 * folded section at the bottom of the card and into a footer button shared
 * with the first-run dialog. Both screens show the SAME list, in the same
 * order, so the ordering lives in one place rather than being reimplemented
 * per screen.
 *
 * @param {import('vue').Ref} protocol  The selected protocol, or null.
 * @param {import('vue').Ref} rows      The limit rows currently on screen, in
 *                                      display order. Only `row.field` is read.
 */
export function useClinicalSources(protocol, rows) {
  const { t } = useI18n()

  /** The source key that supports one field, or null if nothing is recorded. */
  function sourceKeyFor(field) {
    const provenance = unref(protocol)?.provenance
    if (!provenance) return null
    // `suspiciousPatterns.zeroDoses` is one flag in a group the registry cites
    // as a whole, so a dotted field falls back to its parent.
    return provenance[field] ?? provenance[String(field).split('.')[0]] ?? null
  }

  /**
   * Reading order, not registry order: a source is numbered by where the card
   * first leans on it, so the list runs in the order the limits above it do.
   */
  const sourceOrder = computed(() => {
    const p = unref(protocol)
    if (!p?.sources) return []
    const seen = []
    ;(unref(rows) ?? []).forEach((row) => {
      const key = sourceKeyFor(row.field)
      if (key && !seen.includes(key)) seen.push(key)
    })
    const ladderKey = p.provenance?.directionalCompliance
    if (ladderKey && !seen.includes(ladderKey)) seen.push(ladderKey)
    // Cited by the protocol but by no row on this screen: it still supported a
    // number, so it still belongs in the list.
    Object.keys(p.sources).forEach((key) => {
      if (!seen.includes(key)) seen.push(key)
    })
    return seen
  })

  const sourceNumbers = computed(() =>
    Object.fromEntries(sourceOrder.value.map((key, index) => [key, index + 1]))
  )

  const sourceList = computed(() => {
    const p = unref(protocol)
    if (!p?.sources) return []
    return sourceOrder.value.map((key) => ({
      key,
      number: sourceNumbers.value[key],
      ...p.sources[key]
    }))
  })

  function kindLabel(kind) {
    if (kind === 'guideline') return t('userAccount.clinical.kind.guideline')
    if (kind === 'label') return t('userAccount.clinical.kind.label')
    return t('userAccount.clinical.kind.productPolicy')
  }

  /**
   * The whole list as one block of plain text, for pasting into a policy
   * document or an email to a colleague who asked "on whose authority?".
   *
   * Numbered exactly as read, and it carries `kind` — a "Our policy" line and
   * a national guideline look identical once the styling is stripped, and that
   * distinction is the entire reason the list exists.
   */
  const sourcesText = computed(() =>
    sourceList.value
      .map((source) =>
        [
          `[${source.number}] ${source.publisher} — ${kindLabel(source.kind)}`,
          source.reference,
          source.url,
          source.note
        ]
          .filter(Boolean)
          .join('\n')
      )
      .join('\n\n')
  )

  return {
    sourceKeyFor,
    sourceOrder,
    sourceNumbers,
    sourceList,
    kindLabel,
    sourcesText
  }
}
